import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SeoHead from '../components/SeoHead';
import './AdminPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0 });
  const [selected, setSelected] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'tres2025') {
      setAuthenticated(true);
      sessionStorage.setItem('tres_admin', 'true');
    } else {
      alert('Falsches Passwort');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('tres_admin') === 'true') setAuthenticated(true);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/leads`);
      const data = resp.data || [];
      setLeads(data);
      const now = new Date();
      const today = data.filter(l => new Date(l.created_at).toDateString() === now.toDateString()).length;
      const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
      const thisWeek = data.filter(l => new Date(l.created_at) > weekAgo).length;
      setStats({ total: data.length, today, thisWeek });
      setSelected(new Set());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { if (authenticated) fetchLeads(); }, [authenticated, fetchLeads]);

  const sortedLeads = [...leads].sort((a, b) => {
    const aV = a[sortField] || '', bV = b[sortField] || '';
    return sortDir === 'asc' ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
  }).filter(l => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (l.name||'').toLowerCase().includes(s) || (l.email||'').toLowerCase().includes(s) || (l.unit||'').toLowerCase().includes(s);
  });

  const exportCSV = () => {
    const header = 'Name,Email,Telefon,Einheit,Nachricht,Datum,Propstack ID';
    const rows = sortedLeads.map(l => [
      `"${l.name||''}"`,`"${l.email||''}"`,`"${l.phone||''}"`,
      `"${l.unit||''}"`,`"${(l.message||'').replace(/"/g,'""')}"`,
      `"${l.created_at ? new Date(l.created_at).toLocaleString('de-DE') : ''}"`,
      `"${l.propstack_contact_id||''}"`
    ].join(','));
    const blob = new Blob([[header,...rows].join('\n')], {type:'text/csv;charset=utf-8;'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tres-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // Selection
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === sortedLeads.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sortedLeads.map(l => l.id)));
    }
  };

  // Delete
  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    try {
      await axios.post(`${API}/leads/delete-batch`, { ids: [...selected] });
      setDeleteConfirm(null);
      fetchLeads();
    } catch (err) { console.error(err); }
  };

  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API}/leads`);
      setDeleteConfirm(null);
      fetchLeads();
    } catch (err) { console.error(err); }
  };

  const handleDeleteSingle = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`);
      fetchLeads();
    } catch (err) { console.error(err); }
  };

  if (!authenticated) {
    return (
      <>
      <SeoHead page="home" noindex />
      <div className="admin-page" data-testid="admin-login-page">
        <div className="admin-login">
          <h1 className="admin-login-title">TRES IMMO</h1>
          <p className="admin-login-sub">Admin-Bereich</p>
          <form onSubmit={handleLogin} className="admin-login-form">
            <input data-testid="admin-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" className="admin-input" autoFocus />
            <button data-testid="admin-login-btn" type="submit" className="admin-btn-primary">Anmelden</button>
          </form>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
    <SeoHead page="home" noindex />
    <div className="admin-page" data-testid="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <a href="/" className="admin-logo">TRES IMMO</a>
          <span className="admin-badge">Admin</span>
        </div>
        <div className="admin-header-right">
          <button className="admin-btn-outline" onClick={fetchLeads} data-testid="admin-refresh-btn">Aktualisieren</button>
          <button className="admin-btn-outline" onClick={exportCSV} data-testid="admin-csv-btn">CSV Export</button>
          <button className="admin-btn-ghost" onClick={() => { sessionStorage.removeItem('tres_admin'); setAuthenticated(false); }}>Abmelden</button>
        </div>
      </header>
      <main className="admin-main">
        <div className="admin-stats">
          <div className="admin-stat"><span className="admin-stat-val" data-testid="stat-total">{stats.total}</span><span className="admin-stat-label">Gesamt</span></div>
          <div className="admin-stat"><span className="admin-stat-val" data-testid="stat-today">{stats.today}</span><span className="admin-stat-label">Heute</span></div>
          <div className="admin-stat"><span className="admin-stat-val" data-testid="stat-week">{stats.thisWeek}</span><span className="admin-stat-label">Diese Woche</span></div>
        </div>
        <div className="admin-toolbar">
          <input type="text" placeholder="Suchen (Name, Email, Einheit)..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="admin-search" data-testid="admin-search" />
          <span className="admin-count">{sortedLeads.length} Einträge</span>
          <div className="admin-toolbar-actions">
            {selected.size > 0 && (
              <button className="admin-btn-danger" onClick={() => setDeleteConfirm('selected')} data-testid="delete-selected-btn">
                {selected.size} ausgewählt löschen
              </button>
            )}
            {leads.length > 0 && (
              <button className="admin-btn-danger-outline" onClick={() => setDeleteConfirm('all')} data-testid="delete-all-btn">
                Alle löschen
              </button>
            )}
          </div>
        </div>

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="admin-confirm-overlay" data-testid="delete-confirm-modal">
            <div className="admin-confirm-box">
              <p className="admin-confirm-text">
                {deleteConfirm === 'all'
                  ? `Sind Sie sicher, dass Sie ALLE ${leads.length} Einträge unwiderruflich löschen möchten?`
                  : `Sind Sie sicher, dass Sie ${selected.size} ausgewählte Einträge unwiderruflich löschen möchten?`
                }
              </p>
              <div className="admin-confirm-actions">
                <button className="admin-btn-outline" onClick={() => setDeleteConfirm(null)} data-testid="delete-cancel-btn">Abbrechen</button>
                <button className="admin-btn-danger" onClick={deleteConfirm === 'all' ? handleDeleteAll : handleDeleteSelected} data-testid="delete-confirm-btn">
                  Endgültig löschen
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? <div className="admin-loading">Lade Leads...</div> : sortedLeads.length === 0 ? <div className="admin-empty">Keine Leads vorhanden.</div> : (
          <div className="admin-table-wrap">
            <table className="admin-table" data-testid="admin-leads-table">
              <thead><tr>
                <th className="admin-th-check">
                  <input type="checkbox" checked={selected.size === sortedLeads.length && sortedLeads.length > 0} onChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </th>
                <th onClick={() => toggleSort('created_at')}>Datum {sortField==='created_at' ? (sortDir==='asc'?'↑':'↓') : ''}</th>
                <th onClick={() => toggleSort('name')}>Name {sortField==='name' ? (sortDir==='asc'?'↑':'↓') : ''}</th>
                <th onClick={() => toggleSort('email')}>Email {sortField==='email' ? (sortDir==='asc'?'↑':'↓') : ''}</th>
                <th>Telefon</th><th>Einheit</th><th>Nachricht</th><th>Propstack</th><th></th>
              </tr></thead>
              <tbody>
                {sortedLeads.map((l, i) => (
                  <tr key={l.id || i} className={selected.has(l.id) ? 'admin-row-selected' : ''}>
                    <td className="admin-td-check">
                      <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} data-testid={`select-lead-${i}`} />
                    </td>
                    <td className="admin-td-date">{l.created_at ? new Date(l.created_at).toLocaleString('de-DE',{day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'}) : '–'}</td>
                    <td><strong>{l.name}</strong></td>
                    <td><a href={`mailto:${l.email}`}>{l.email}</a></td>
                    <td>{l.phone || '–'}</td>
                    <td>{l.unit || '–'}</td>
                    <td className="admin-td-msg" title={l.message}>{l.message ? (l.message.length > 60 ? l.message.slice(0,60)+'...' : l.message) : '–'}</td>
                    <td>{l.propstack_contact_id ? <span className="admin-ps-badge">#{l.propstack_contact_id}</span> : <span className="admin-ps-none">–</span>}</td>
                    <td>
                      <button className="admin-btn-del-row" onClick={() => handleDeleteSingle(l.id)} title="Löschen" data-testid={`delete-lead-${i}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
    </>
  );
};

export default AdminPage;
